import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { ReactNode, useState } from 'react';
import { useIntl } from 'react-intl';

import { apiPostStories, APIPostStoriesPayload, Story } from '@/shared/api';
import { defaultStoriesByLocaleOptions } from '@/shared/queries';
import { Button } from '@/shared/ui/button';
import { Card, CardContent } from '@/shared/ui/card';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/shared/ui/dialog';
import { ScrollArea } from '@/shared/ui/scroll-area';
import { Language } from '@/shared/utils';

import { useLanguage } from './language-provider';
import { LanguageSelect } from './language-select';

type StoryAddNewDialogProps = {
  children: ReactNode;
};

export const AddNewStoryDialog = ({ children }: StoryAddNewDialogProps) => {
  const intl = useIntl();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [storyTemplate, setStoryTemplate] = useState<string | null>(null);
  const { language } = useLanguage();
  const [templateLanguage, setTemplateLanguage] = useState<Language>(language);

  const query = useQuery(defaultStoriesByLocaleOptions(templateLanguage));

  const mutation = useMutation<Story, Error, APIPostStoriesPayload>({
    mutationFn: (payload) => {
      return apiPostStories(payload).then(({ data }) => data);
    },
    onSuccess: ({ id }) => {
      queryClient.invalidateQueries({
        queryKey: ['stories'],
      });
      navigate({ to: '/stories/$storyId', params: { storyId: id } });
    },
  });

  return (
    <Dialog>
      <DialogTrigger asChild>{children}</DialogTrigger>

      <DialogContent className="sm:max-w-[660px]">
        <DialogHeader>
          <DialogTitle>
            {intl.formatMessage({ id: 'addNewStory.dialogTitle' })}
          </DialogTitle>
          <DialogDescription>
            {intl.formatMessage({ id: 'addNewStory.dialogDescription' })}
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-col gap-4">
          <div>
            <p className="mb-2 text-sm">
              {intl.formatMessage({ id: 'addNewStory.languageSelect' })}
            </p>
            <LanguageSelect
              value={templateLanguage}
              onValueChange={setTemplateLanguage}
            />
          </div>

          <ScrollArea className="h-[250px]">
            <div className="grid grid-cols-1 gap-4 py-4 md:grid-cols-2">
              {query.data?.data.map((defaultStory) => (
                <button
                  key={defaultStory.id}
                  onClick={() => setStoryTemplate(defaultStory.id)}
                >
                  <Card
                    key={defaultStory.id}
                    className="min-w-[280px]"
                    selected={storyTemplate === defaultStory.id}
                  >
                    <CardContent className="p-8">
                      <span className="text-2xl">{defaultStory.displayName}</span>
                    </CardContent>
                  </Card>
                </button>
              ))}

              <button onClick={() => setStoryTemplate(null)}>
                <Card className="h-full min-w-[280px]" selected={storyTemplate === null}>
                  <CardContent className="p-8">
                    <span className="text-2xl">
                      {intl.formatMessage({
                        id: 'storyTemplate.custom',
                      })}
                    </span>
                  </CardContent>
                </Card>
              </button>
            </div>
          </ScrollArea>
        </div>

        <DialogFooter className="gap-y-4">
          <DialogClose asChild>
            <Button variant="secondary">
              {intl.formatMessage({ id: 'button.close' })}
            </Button>
          </DialogClose>

          <DialogClose asChild>
            <Button
              type="submit"
              onClick={() =>
                mutation.mutate({
                  ...(storyTemplate && { defaultStoryId: storyTemplate }),
                })
              }
            >
              {intl.formatMessage({
                id: mutation.isPending ? 'button.loading' : 'button.add',
              })}
            </Button>
          </DialogClose>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
